'use client';

import { memo } from 'react';

import { Button, Chip } from '@nextui-org/react';
import { Pencil } from 'lucide-react';
import NextImage from 'next/image';

import { McButton } from '@/components/masterclass/mc-button';
import { McLikeBtn } from '@/components/masterclass/mc-like-btn/mc-like-btn';
import { IMasterclass } from '@/entities/masterclass/types';
import { Link } from '@/shared/navigation';
import { LAZY_IMAGE_LOADING } from '@/shared/const/image-props';
import {
  Carousel,
  CarouselIndicator,
  CarouselMainContainer,
  CarouselThumbsContainer,
  SliderMainItem,
} from '@/components/extencions/carousel';
import { cn } from '@/shared/utils/utils';
import { formatPrice } from '@/shared/utils/format-price';
import { formatReviewsCount } from '@/shared/utils/plural-ru';
import { useIsAdmin } from '@/shared/hooks/is-admin';
import { getToySizeFromDescription } from '@/shared/utils/get-toy-size-from-description';
import { getMasterclassPurchaseCount } from '@/widgets/masterclasses/utils/sort-masterclasses';
import {
  formatPurchaseCountBadge,
  formatPurchaseCountBadgeTitle,
  isPopularMasterclassByPurchases,
} from '@/shared/constants/masterclass-popularity';

const McCardBadges = ({
  masterclass,
  size,
}: {
  masterclass: IMasterclass;
  size?: string | null;
}) => {
  const purchaseCount = getMasterclassPurchaseCount(masterclass);
  const showPopularBadge = isPopularMasterclassByPurchases(purchaseCount);

  if (!size && !masterclass.discount && !showPopularBadge) {
    return null;
  }

  return (
    <div className="absolute bottom-2 left-2 z-20 flex h-auto flex-col justify-end gap-1 text-xs">
      {size && (
        <Chip
          classNames={{
            content: 'pl-1 pr-1',
          }}
          className="h-5 rotate-[-3deg] bg-lime-700 text-xs text-white"
        >
          {size} см
        </Chip>
      )}
      {!!masterclass.discount && (
        <Chip
          classNames={{
            content: 'pl-1 pr-1 ',
          }}
          color="danger"
          className="h-5 rotate-[-3deg] text-xs"
        >
          –{masterclass.discount?.toFixed(0)}%
        </Chip>
      )}
      {showPopularBadge && (
        <Chip
          classNames={{
            content: 'px-1 text-[10px] leading-none',
          }}
          className="h-5 max-w-[calc(100%-0.5rem)] rotate-[-3deg] bg-primary text-gray-900"
          title={formatPurchaseCountBadgeTitle(purchaseCount)}
        >
          {formatPurchaseCountBadge(purchaseCount)}
        </Chip>
      )}
    </div>
  );
};

// eslint-disable-next-line react/display-name
export const MasterclassCard = memo(
  ({
    masterclass,
    imageHeight: _imageHeight = 250,
    withCartButton = true,
    withLikeButton = true,
    isPending: _isPending = false,
    withCarousel = true,
    headingLevel = 'h3',
    priority = false,
  }: {
    masterclass: IMasterclass;
    imageHeight?: number;
    withCartButton?: boolean;
    withLikeButton?: boolean;
    isPending?: boolean;
    withCarousel?: boolean;
    /**
     * Для карточек первого экрана (LCP): eager-загрузка c fetchpriority=high
     * вместо lazy. Включать только для видимых без скролла карточек.
     */
    priority?: boolean;
    /**
     * Уровень заголовка с названием МК. Задаётся контекстом для правильной
     * иерархии: в каталоге (сетка сразу под H1) — 'h2', внутри секций с
     * собственным H2-заголовком (похожие/популярные) — 'h3' (по умолчанию).
     */
    headingLevel?: 'h2' | 'h3';
  }) => {
    const { isAdmin } = useIsAdmin();
    const Heading = headingLevel;

    const size =
      getToySizeFromDescription(masterclass.description) ||
      getToySizeFromDescription(masterclass.contents) ||
      getToySizeFromDescription(masterclass.materials);

    return (
      <Link
        className="relative justify-between h-full flex w-full flex-none flex-col gap-1"
        href={`/masterclasses/${masterclass.wordId}`}
        aria-label={masterclass.name}
      >
        {withCarousel && (masterclass.images?.length ?? 0) > 1 ? (
          <Carousel className="relative rounded-large">
            <div
              className={cn(
                'absolute z-20 bottom-[10px] left-1/2 -translate-x-1/2',
              )}
            >
              <CarouselThumbsContainer className="gap-x-1">
                {masterclass.images
                  ?.slice(0, 3)
                  .map((image, index) => (
                    <CarouselIndicator key={index} index={index} />
                  ))}
              </CarouselThumbsContainer>
            </div>
            <CarouselMainContainer>
              {masterclass.images?.slice(0, 3).map((image, index) => (
                <SliderMainItem
                  key={index}
                  className="relative bg-[#f7e6af]/30 rounded-large pr-0 w-full h-[200px] md:h-[280px]"
                >
                  <NextImage
                    fill
                    sizes="(max-width: 768px) 50vw, 25vw"
                    quality={65}
                    {...(priority && index === 0
                      ? { priority: true }
                      : { loading: LAZY_IMAGE_LOADING })}
                    alt={masterclass.name}
                    src={masterclass.images?.[index].url ?? ''}
                    className="object-cover rounded-large"
                  />
                </SliderMainItem>
              ))}
            </CarouselMainContainer>
            <McCardBadges masterclass={masterclass} size={size} />
          </Carousel>
        ) : (
          <div className="relative rounded-large w-full h-[200px] md:h-[280px]">
            <NextImage
              fill
              sizes="(max-width: 768px) 50vw, 25vw"
              quality={65}
              {...(priority
                ? { priority: true }
                : { loading: LAZY_IMAGE_LOADING })}
              alt={masterclass.name}
              src={masterclass.images?.[0].url ?? ''}
              className="object-cover rounded-large"
            />
            <McCardBadges masterclass={masterclass} size={size} />
          </div>
        )}

        {(withLikeButton || isAdmin) && (
          <div className="group inline-flex items-center justify-center absolute right-2 top-2 z-20">
            {withLikeButton ? (
              <McLikeBtn withBg mc={masterclass} size={18} />
            ) : null}
            {isAdmin && !withLikeButton && (
              <Link
                href={`/admin-front/masterclasses/${masterclass.wordId}/edit`}
                aria-label="Редактировать мастер-класс"
              >
                <Button
                  isIconOnly
                  radius="full"
                  aria-label="Редактировать мастер-класс"
                >
                  <Pencil strokeWidth={1} size={22} />
                </Button>
              </Link>
            )}
          </div>
        )}

        <div className="mt-1 flex flex-col gap-0.5 px-1">
          {/* Название — сразу под фото, крупно */}
          <Heading className="min-w-0 line-clamp-2 text-medium leading-tight text-default-800">
            {masterclass.name}
          </Heading>

          {/* Цена (со старой ценой и % скидки) */}
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <p className="text-large font-bold text-gray-900">
              {formatPrice(masterclass.finalPrice)} ₽
            </p>
            {masterclass.price !== masterclass.finalPrice && (
              <>
                <p className="text-small text-default-400 line-through">
                  {formatPrice(masterclass.price)} ₽
                </p>
                {!!masterclass.discount && (
                  <span className="text-small font-semibold text-danger">
                    –{masterclass.discount.toFixed(0)}%
                  </span>
                )}
              </>
            )}
          </div>

          {/* Рейтинг/отзывы — только если отзывы есть */}
          {!!masterclass.reviewsCount && (
            <div className="mt-0.5 flex min-w-0 items-center gap-1 text-small text-default-500">
              <svg
                fill="#FBBF24"
                stroke="#FBBF24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                role="img"
                className="shrink-0"
                width="16"
                height="16"
                viewBox="0 0 24 24"
              >
                <path
                  fill="#FBBF24"
                  d="M9.153 5.408C10.42 3.136 11.053 2 12 2c.947 0 1.58 1.136 2.847 3.408l.328.588c.36.646.54.969.82 1.182c.28.213.63.292 1.33.45l.636.144c2.46.557 3.689.835 3.982 1.776c.292.94-.546 1.921-2.223 3.882l-.434.507c-.476.557-.715.836-.822 1.18c-.107.345-.071.717.001 1.46l.066.677c.253 2.617.38 3.925-.386 4.506c-.766.582-1.918.051-4.22-1.009l-.597-.274c-.654-.302-.981-.452-1.328-.452c-.347 0-.674.15-1.328.452l-.596.274c-2.303 1.06-3.455 1.59-4.22 1.01c-.767-.582-.64-1.89-.387-4.507l.066-.676c.072-.744.108-1.116 0-1.46c-.106-.345-.345-.624-.821-1.18l-.434-.508c-1.677-1.96-2.515-2.941-2.223-3.882c.293-.941 1.523-1.22 3.983-1.776l.636-.144c.699-.158 1.048-.237 1.329-.45c.28-.213.46-.536.82-1.182z"
                ></path>
              </svg>
              <span className="font-medium text-default-600">
                {masterclass.rate?.toFixed(1) ?? '0.0'}
              </span>
              {/* default-600, не 400: контраст «· N отзывов» ≥ 4.5:1 (WCAG AA) */}
              <span className="truncate text-default-600">
                · {formatReviewsCount(masterclass.reviewsCount)}
              </span>
            </div>
          )}
        </div>
        {withCartButton && (
          <McButton
            masterclass={masterclass}
            showReviewPromoButton={false}
            className="mt-auto"
          />
        )}
      </Link>
    );
  },
);
